import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mic2 } from 'lucide-react';
import { useSpeechVoice } from '../../contexts/SpeechVoiceContext';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

const scoreVoice = (v: SpeechSynthesisVoice) => {
    const name = (v.name || '').toLowerCase();
    const lang = (v.lang || '').toLowerCase();

    let s = 0;
    if (lang.startsWith('en')) s += 10;

    if (name.includes('natural')) s += 60;
    if (name.includes('neural')) s += 50;
    if (name.includes('enhanced')) s += 30;
    if (name.includes('google')) s += 35;
    if (name.includes('microsoft')) s += 25;
    if (name.includes('siri')) s += 20;

    const avoid = ['bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos', 'whisper', 'robot', 'novelty'];
    for (const k of avoid) {
        if (name.includes(k)) s -= 100;
    }

    return s;
};

export const VoiceSelector: React.FC = () => {
    const { voices, selectedVoice, setVoiceURI } = useSpeechVoice();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const [desktopPos, setDesktopPos] = useState<{ top: number; left: number; width: number } | null>(null);

    useOnClickOutside(dropdownRef as React.RefObject<HTMLElement>, () => setIsOpen(false));

    const relevantVoices = useMemo(() => {
        if (!voices || voices.length === 0) return [];
        const english = voices.filter((v) => v.lang?.toLowerCase().startsWith('en'));
        return english.length > 0 ? english : voices;
    }, [voices]);

    const recommendedVoices = useMemo(() => {
        const dedup = new Map<string, SpeechSynthesisVoice>();
        for (const v of relevantVoices) {
            dedup.set(v.voiceURI, v);
        }

        const sorted = Array.from(dedup.values()).sort((a, b) => scoreVoice(b) - scoreVoice(a));
        let top = sorted.slice(0, 10);

        if (selectedVoice && !top.some((v) => v.voiceURI === selectedVoice.voiceURI)) {
            top = [selectedVoice, ...top.slice(0, 9)];
        }

        return top;
    }, [relevantVoices, selectedVoice]);

    const filteredVoices = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return recommendedVoices;
        return recommendedVoices.filter((v) => {
            const name = (v.name || '').toLowerCase();
            const lang = (v.lang || '').toLowerCase();
            return name.includes(q) || lang.includes(q);
        });
    }, [query, recommendedVoices]);

    const handleSelect = (voiceURI: string) => {
        setVoiceURI(voiceURI);
        setIsOpen(false);
    };

    useEffect(() => {
        if (!isOpen) {
            setQuery('');
            setDesktopPos(null);
            return;
        }

        // Compute a safe, clamped position for tablet/laptop so the popover is never cropped.
        const computeDesktopPos = () => {
            const isDesktop = window.matchMedia('(min-width: 768px)').matches;
            if (!isDesktop) return;
            const el = dropdownRef.current;
            if (!el) return;

            const r = el.getBoundingClientRect();
            const desiredWidth = 320;
            const width = Math.min(desiredWidth, window.innerWidth - 16);

            // Align right edge of popover with button, but clamp within viewport.
            const rawLeft = r.right - width;
            const left = Math.max(8, Math.min(rawLeft, window.innerWidth - width - 8));
            const top = Math.max(56, r.bottom + 8);

            setDesktopPos({ top, left, width });
        };

        computeDesktopPos();
        window.addEventListener('resize', computeDesktopPos);
        window.addEventListener('scroll', computeDesktopPos, true);

        const t = window.setTimeout(() => searchRef.current?.focus(), 0);
        return () => {
            window.clearTimeout(t);
            window.removeEventListener('resize', computeDesktopPos);
            window.removeEventListener('scroll', computeDesktopPos, true);
        };
    }, [isOpen]);

    if (!voices || voices.length === 0) {
        return null;
    }

    return (
        <div className="relative z-[60]" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Select voice"
                className={`
          flex items-center justify-center p-2 rounded-full
          transition-colors outline-none
          bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200
          focus:ring-2 focus:ring-blue-200
          ${isOpen ? 'bg-blue-100' : ''}
        `}
                title="Select Voice"
            >
                <Mic2 className="w-4 h-4 flex-shrink-0" />
            </button>

            {isOpen && (
                <>
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 z-[55] bg-black/10 md:hidden"
                        aria-label="Close voice selector"
                    />

                    {/* Mobile: full-width sheet */}
                    <div className="fixed left-2 right-2 top-16 z-[60] w-auto max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-xl border border-blue-100 py-2 md:hidden">
                        <div className="px-3">
                            <div className="text-xs font-semibold text-blue-700">Recommended voices</div>
                            <div className="mt-2">
                                <input
                                    ref={searchRef}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search"
                                    aria-label="Search voices"
                                    className="w-full text-sm px-3 py-1.5 rounded-lg border border-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                />
                            </div>
                        </div>

                        <div className="mt-2">
                            {filteredVoices.length === 0 ? (
                                <div className="px-3 py-6 text-sm text-gray-400 text-center">
                                    No voices found
                                </div>
                            ) : (
                                filteredVoices.map((voice) => (
                                    <button
                                        key={voice.voiceURI}
                                        onClick={() => handleSelect(voice.voiceURI)}
                                        className={`
                      w-full text-left px-3 py-1.5 text-sm flex items-center justify-between
                      transition-colors
                      ${selectedVoice?.voiceURI === voice.voiceURI
                                                ? 'text-blue-700 bg-blue-50 font-medium'
                                                : 'text-gray-800 hover:bg-blue-50'}
                    `}
                                    >
                                        <div className="flex flex-col truncate pr-2">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="truncate">{voice.name}</span>
                                                {voice.lang?.toLowerCase().startsWith('en-in') && (
                                                    <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                                        India
                                                    </span>
                                                )}
                                                {scoreVoice(voice) >= 70 && (
                                                    <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white">
                                                        Best
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-[10px] text-gray-400 truncate">{voice.lang}</span>
                                        </div>
                                        {selectedVoice?.voiceURI === voice.voiceURI && (
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Tablet/Laptop: anchored popover, clamped within viewport */}
                    {desktopPos && (
                        <div
                            className="fixed z-[60] max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-blue-100 py-2 hidden md:block"
                            style={{ top: desktopPos.top, left: desktopPos.left, width: desktopPos.width }}
                        >
                            <div className="px-3">
                                <div className="text-xs font-semibold text-blue-700">Recommended voices</div>
                                <div className="mt-2">
                                    <input
                                        ref={searchRef}
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search"
                                        aria-label="Search voices"
                                        className="w-full text-sm px-3 py-1.5 rounded-lg border border-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                    />
                                </div>
                            </div>

                            <div className="mt-2">
                                {filteredVoices.length === 0 ? (
                                    <div className="px-3 py-6 text-sm text-gray-400 text-center">
                                        No voices found
                                    </div>
                                ) : (
                                    filteredVoices.map((voice) => (
                                        <button
                                            key={voice.voiceURI}
                                            onClick={() => handleSelect(voice.voiceURI)}
                                            className={`
                      w-full text-left px-3 py-1.5 text-sm flex items-center justify-between
                      transition-colors
                      ${selectedVoice?.voiceURI === voice.voiceURI
                                                    ? 'text-blue-700 bg-blue-50 font-medium'
                                                    : 'text-gray-800 hover:bg-blue-50'}
                    `}
                                        >
                                            <div className="flex flex-col truncate pr-2">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="truncate">{voice.name}</span>
                                                    {voice.lang?.toLowerCase().startsWith('en-in') && (
                                                        <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                                            India
                                                        </span>
                                                    )}
                                                    {scoreVoice(voice) >= 70 && (
                                                        <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white">
                                                            Best
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-[10px] text-gray-400 truncate">{voice.lang}</span>
                                            </div>
                                            {selectedVoice?.voiceURI === voice.voiceURI && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
